import { gql } from '@apollo/client/core';
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';

const ADD_SINGLE = gql`
  mutation AddManualOutstandingCourse($input: AddManualOutstandingCourseInput!) {
    addManualOutstandingCourse(input: $input) { success message code }
  }
`;

const DELETE_SINGLE = gql`
  mutation DeleteManualOutstandingCourse($id: ID!) {
    deleteManualOutstandingCourse(id: $id) { success message code }
  }
`;

const BULK_DELETE = gql`
  mutation BulkDeleteManualOutstandingCourse($ids: [ID!]!) {
    bulkDeleteManualOutstandingCourse(ids: $ids) { success message code }
  }
`;

const BULK_ADD_BY_STUDENTS = gql`
  mutation BulkAddManualOutstandingByStudents($input: BulkManualOutstandingByStudentsInput!) {
    bulkAddManualOutstandingByStudents(input: $input) { success message code }
  }
`;

const BULK_ADD_BY_SCOPE = gql`
  mutation BulkAddManualOutstandingByScope($input: BulkManualOutstandingByScopeInput!) {
    bulkAddManualOutstandingByScope(input: $input) { success message code }
  }
`;

const REMOVE_BY_SCOPE = gql`
  mutation RemoveManualOutstandingByScope($input: RemoveManualOutstandingByScopeInput!) {
    removeManualOutstandingByScope(input: $input) { success message code }
  }
`;

const CLEANUP_RESOLVED = gql`
  mutation CleanupResolvedManualOutstanding {
    cleanupResolvedManualOutstanding { success message code }
  }
`;

function wrapMutation(toast: ReturnType<typeof useToast>, mutationKey: string) {
  return async(res: Awaited<ReturnType<ReturnType<typeof useMutation>['mutate']>>) => {
    const data = (res?.data as Record<string, { success: boolean; message: string }>)?.[mutationKey];
    if (data?.success) {
      toast.add({ severity: 'success', summary: 'Success', detail: data.message, life: 3000 });
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: data?.message || 'Operation failed', life: 5000 });
    }
    return data;
  };
}

export function useManualOutstandingMutations() {
  const toast = useToast();

  const { mutate: addMutate, loading: adding } = useMutation(ADD_SINGLE);
  const { mutate: deleteMutate, loading: deleting } = useMutation(DELETE_SINGLE);
  const { mutate: bulkDeleteMutate, loading: bulkDeleting } = useMutation(BULK_DELETE);
  const { mutate: bulkByStudentsMutate, loading: bulkAddingByStudents } = useMutation(BULK_ADD_BY_STUDENTS);
  const { mutate: bulkByScopeMutate, loading: bulkAddingByScope } = useMutation(BULK_ADD_BY_SCOPE);
  const { mutate: removeByScopeMutate, loading: removingByScope } = useMutation(REMOVE_BY_SCOPE);
  const { mutate: cleanupMutate, loading: cleaningUp } = useMutation(CLEANUP_RESOLVED);

  async function addManualOutstanding(input: { student_id: string; course_id: string; status: string; curriculum_level?: string; reason?: string }) {
    try {
      const res = await addMutate({ input });
      return await wrapMutation(toast, 'addManualOutstandingCourse')(res);
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function deleteManualOutstanding(id: string) {
    try {
      const res = await deleteMutate({ id });
      return await wrapMutation(toast, 'deleteManualOutstandingCourse')(res);
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function bulkDeleteManualOutstanding(ids: string[]) {
    try {
      const res = await bulkDeleteMutate({ ids });
      return await wrapMutation(toast, 'bulkDeleteManualOutstandingCourse')(res);
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function bulkAddByStudents(input: { student_ids: string[]; course_ids: string[]; status: string; curriculum_level?: string; reason?: string }) {
    try {
      const res = await bulkByStudentsMutate({ input });
      return await wrapMutation(toast, 'bulkAddManualOutstandingByStudents')(res);
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function bulkAddByScope(input: { scope_type: string; scope_id: string; levels: string[]; course_ids: string[]; status: string; curriculum_level?: string; reason?: string }) {
    try {
      const res = await bulkByScopeMutate({ input });
      return await wrapMutation(toast, 'bulkAddManualOutstandingByScope')(res);
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function removeByScope(input: { scope_type: string; scope_id: string; levels: string[]; course_ids: string[] }) {
    try {
      const res = await removeByScopeMutate({ input });
      return await wrapMutation(toast, 'removeManualOutstandingByScope')(res);
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function cleanupResolved() {
    try {
      const res = await cleanupMutate({});
      return await wrapMutation(toast, 'cleanupResolvedManualOutstanding')(res);
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  return {
    addManualOutstanding, adding,
    deleteManualOutstanding, deleting,
    bulkDeleteManualOutstanding, bulkDeleting,
    bulkAddByStudents, bulkAddingByStudents,
    bulkAddByScope, bulkAddingByScope,
    removeByScope, removingByScope,
    cleanupResolved, cleaningUp,
  };
}
